// ==== SEASONS + BREEDING PAIRS ====
// Split from the original single tempdashboard.js (originally lines 3050-4080).
// Loaded as a plain classic <script defer> alongside the other tempdashboard-*.js files --
// they all share one global scope (like separate parts of one big file), so functions and
// top-level let/const declared in any of these files are visible to all the others, as long
// as the <script> tags stay in the same order every one of these files was split in.

  // ---- SEASONS: a real managed list now (name, year, notes, one active at
  // a time). Season is a label/organizer on each pair (filterable on the
  // Breeding Pairs view), not a visibility gate -- an earlier version hid
  // every pair not in the active season, which made pairs unreachable the
  // moment their season got deleted.
  let seasons = {}; // seasonId -> data
  let editingSeasonId = null;

  function activeSeasonId() {
    return Object.keys(seasons).find(id => seasons[id].isActive) || null;
  }

  function listenForSeasons() {
    // Filtered by ownerUid, not orgId -- the seasons rule checks
    // resource.data.ownerUid, and Firestore rejects a whole list query with
    // "Missing or insufficient permissions" unless the query's own filter
    // guarantees every possible result satisfies the rule (matches the
    // same ownerUid-filter pattern already used for the organizations
    // query above).
    const query = isAdmin ? db.collection('seasons') : db.collection('seasons').where('ownerUid', '==', auth.currentUser.uid);
    query.onSnapshot(snapshot => {
      seasons = {};
      snapshot.forEach(doc => { seasons[doc.id] = doc.data(); });
      document.getElementById('db-nav-seasons-count').textContent = Object.keys(seasons).length || '';
      renderSeasonsList();
      renderPairsList();
    }, error => {
      console.error('Seasons listener error:', error);
      showToast('⚠️ Could not load seasons: ' + error.message);
    });
  }

  // Season status: Active (the one season flagged isActive, which the rest of
  // the app -- Home, Breeding Pairs -- already keys off), Upcoming or
  // Completed. New seasons store it in `status`; older seasons that predate
  // the field fall back to their dates, then their year.
  function seasonStatusOf(sn) {
    if (sn.isActive) return 'active';
    if (sn.status === 'completed' || sn.status === 'upcoming') return sn.status;
    const now = Date.now();
    const end = sn.endDate ? new Date(sn.endDate + 'T23:59:59').getTime() : NaN;
    const start = sn.startDate ? new Date(sn.startDate + 'T00:00:00').getTime() : NaN;
    if (!isNaN(end) && end < now) return 'completed';
    if (!isNaN(start) && start > now) return 'upcoming';
    if ((sn.year || 0) && sn.year < new Date().getFullYear()) return 'completed';
    return 'upcoming';
  }
  const SEASON_STATUS_META = {
    active: { label: 'Active', color: '#19a974' },
    upcoming: { label: 'Upcoming', color: '#f4a623' },
    completed: { label: 'Completed', color: '#8a97a3' }
  };
  function fmtSeasonDate(str) {
    if (!str) return '—';
    const t = new Date(str + 'T00:00:00').getTime();
    return isNaN(t) ? '—' : fmtDateOnly(t);
  }

  let seasonsPerPage = 10;
  let currentSeasonPage = 1;
  let seasonStatusFilter = 'all'; // 'all' | 'active' | 'upcoming' | 'completed'

  function renderSeasonsList() {
    const body = document.getElementById('db-seasons-body');
    const empty = document.getElementById('db-seasons-empty');
    const tableCard = document.getElementById('szn-table-card');
    const footer = document.getElementById('szn-footer');
    const allIds = Object.keys(seasons);
    document.getElementById('db-seasons-count').textContent = allIds.length ? '(' + allIds.length + ')' : '';

    // Summary cards -- counts of the real seasons, by status.
    const counts = { active: 0, upcoming: 0, completed: 0 };
    allIds.forEach(id => { counts[seasonStatusOf(seasons[id])]++; });
    const setStat = (id, n) => { const el = document.getElementById(id); if (el) el.textContent = n; };
    setStat('szn-stat-total', allIds.length);
    setStat('szn-stat-active', counts.active);
    setStat('szn-stat-upcoming', counts.upcoming);
    setStat('szn-stat-completed', counts.completed);

    const term = ((document.getElementById('db-seasons-search') || {}).value || '').trim().toLowerCase();
    let ids = allIds.filter(id => {
      const sn = seasons[id];
      if (seasonStatusFilter !== 'all' && seasonStatusOf(sn) !== seasonStatusFilter) return false;
      if (!term) return true;
      return (sn.name || '').toLowerCase().includes(term) || (sn.notes || '').toLowerCase().includes(term);
    });
    // Newest year first; within a year, by start date.
    ids.sort((a, b) => {
      const sa = seasons[a], sb = seasons[b];
      if ((sb.year || 0) !== (sa.year || 0)) return (sb.year || 0) - (sa.year || 0);
      return (sa.startDate || '').localeCompare(sb.startDate || '') || (sa.name || '').localeCompare(sb.name || '');
    });

    const showingEl = document.getElementById('db-seasons-showing');
    if (showingEl) showingEl.textContent = '';
    if (ids.length === 0) {
      empty.style.display = 'block';
      document.getElementById('db-seasons-empty-title').textContent = allIds.length === 0 ? 'No seasons yet.' : 'No seasons match your search/filter.';
      document.getElementById('db-seasons-empty-text').textContent = allIds.length === 0
        ? 'Create a season to start tracking pairs against it.' : 'Try a different search term or status.';
      tableCard.style.display = 'none';
      footer.style.display = 'none';
      body.innerHTML = '';
      return;
    }
    empty.style.display = 'none';
    tableCard.style.display = '';
    footer.style.display = '';

    const totalPages = seasonsPerPage === Infinity ? 1 : Math.max(1, Math.ceil(ids.length / seasonsPerPage));
    if (currentSeasonPage > totalPages) currentSeasonPage = totalPages;
    const startIdx = seasonsPerPage === Infinity ? 0 : (currentSeasonPage - 1) * seasonsPerPage;
    const pageIds = seasonsPerPage === Infinity ? ids : ids.slice(startIdx, startIdx + seasonsPerPage);
    renderSeasonsPagination(totalPages);
    if (showingEl) {
      showingEl.textContent = 'Showing ' + (startIdx + 1) + ' to ' + (startIdx + pageIds.length) + ' of ' + ids.length +
        ' season' + (ids.length !== 1 ? 's' : '');
    }

    body.innerHTML = '';
    pageIds.forEach((seasonId, i) => {
      const sn = seasons[seasonId];
      const status = seasonStatusOf(sn);
      const meta = SEASON_STATUS_META[status];
      const startStr = fmtSeasonDate(sn.startDate);
      const endStr = fmtSeasonDate(sn.endDate);
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td class="szn-col-num">${startIdx + i + 1}</td>
        <td class="szn-col-name"><div class="szn-name">${sn.name}</div><div class="szn-dates-m">${startStr} – ${endStr}</div></td>
        <td class="szn-col-start">${startStr}</td>
        <td class="szn-col-end">${endStr}</td>
        <td class="szn-col-status"><span class="pgn-badge szn-badge--${status}">${meta.label}</span></td>
        <td class="szn-col-notes">${sn.notes ? sn.notes : '—'}</td>
        <td class="szn-col-actions">
          <div class="szn-actions">
            <button type="button" class="szn-btn season-view-btn"><i class="fa-solid fa-eye"></i> View</button>
            <button type="button" class="szn-btn season-edit-item"><i class="fa-solid fa-pen"></i> Edit</button>
            <div class="szn-kebab-wrap">
              <button type="button" class="ltc-kebab-btn pgn-kebab" aria-label="More actions"><i class="fa-solid fa-ellipsis-vertical"></i></button>
              <div class="ltc-kebab-menu">
                ${sn.isActive ? '' : '<div class="ltc-kebab-menu-item season-activate-item"><i class="fa-solid fa-circle-play"></i> Set as Active</div><div class="ltc-kebab-menu-divider"></div>'}
                <div class="ltc-kebab-menu-item danger season-delete-item"><i class="fa-solid fa-trash"></i> Delete Season</div>
              </div>
            </div>
          </div>
        </td>`;
      const kebabBtn = tr.querySelector('.ltc-kebab-btn');
      const kebabMenu = tr.querySelector('.ltc-kebab-menu');
      kebabBtn.onclick = (e) => {
        e.stopPropagation();
        const wasOpen = kebabMenu.classList.contains('open');
        document.querySelectorAll('.ltc-kebab-menu.open').forEach(m => m.classList.remove('open'));
        if (!wasOpen) { kebabMenu.classList.add('open'); keepKebabMenuInView(kebabMenu); }
      };
      tr.querySelector('.season-view-btn').onclick = () => openSeasonView(seasonId);
      tr.querySelector('.season-edit-item').onclick = () => openEditSeasonModal(seasonId, sn);
      const activateBtn = tr.querySelector('.season-activate-item');
      if (activateBtn) activateBtn.onclick = () => { kebabMenu.classList.remove('open'); setActiveSeason(seasonId); };
      tr.querySelector('.season-delete-item').onclick = () => { kebabMenu.classList.remove('open'); deleteSeason(seasonId); };
      body.appendChild(tr);
    });
  }

  function renderSeasonsPagination(totalPages) {
    const el = document.getElementById('db-seasons-pagination');
    if (!el) return;
    el.innerHTML = '';
    if (totalPages <= 1) return;
    const prevBtn = document.createElement('button');
    prevBtn.textContent = '← Prev';
    prevBtn.disabled = currentSeasonPage === 1;
    prevBtn.onclick = () => { currentSeasonPage--; renderSeasonsList(); };
    el.appendChild(prevBtn);
    for (let i = 1; i <= totalPages; i++) {
      const pageBtn = document.createElement('button');
      pageBtn.textContent = i;
      if (i === currentSeasonPage) pageBtn.className = 'active';
      pageBtn.onclick = () => { currentSeasonPage = i; renderSeasonsList(); };
      el.appendChild(pageBtn);
    }
    const nextBtn = document.createElement('button');
    nextBtn.textContent = 'Next →';
    nextBtn.disabled = currentSeasonPage === totalPages;
    nextBtn.onclick = () => { currentSeasonPage++; renderSeasonsList(); };
    el.appendChild(nextBtn);
  }

  async function deleteSeason(seasonId) {
    const sn = seasons[seasonId];
    if (!sn) return;
    const warningMsg = sn.isActive
      ? 'Delete season "' + sn.name + '"? This is your ACTIVE season — deleting it will automatically switch Breeding Pairs to your next most recent season (if you have one). Pairs already using "' + sn.name + '" stay as-is, just hidden until it\'s made active again.'
      : 'Delete season "' + sn.name + '"? Pairs already using it stay as-is, but won\'t show on Breeding Pairs unless this season is made active again.';
    if (!(await showConfirmModal(warningMsg, 'Delete season?'))) return;
    try {
      const wasActive = sn.isActive;
      await db.collection('seasons').doc(seasonId).delete();
      delete seasons[seasonId];
      // Deleting the active season would otherwise leave zero active
      // seasons, silently hiding EVERY pair (not just this season's) --
      // auto-promote the next most recent remaining season instead.
      const remainingIds = Object.keys(seasons).sort((a, b) => (seasons[b].year || 0) - (seasons[a].year || 0));
      if (wasActive && remainingIds.length > 0) {
        await setActiveSeason(remainingIds[0]);
        showToast('🗑️ Season deleted — switched active season to ' + seasons[remainingIds[0]].name);
      } else {
        renderSeasonsList();
        renderPairsList();
        showToast('🗑️ Season deleted');
      }
    }
    catch (e) { showToast('Could not delete: ' + e.message); }
  }

  // Read-only details for one season.
  let viewingSeasonId = null;
  function openSeasonView(seasonId) {
    const sn = seasons[seasonId];
    if (!sn) return;
    viewingSeasonId = seasonId;
    const status = seasonStatusOf(sn);
    const pairCount = Object.keys(breedingPairs).filter(id => breedingPairs[id].seasonId === seasonId).length;
    const trainingCount = Object.keys(trainings).filter(id => trainings[id].seasonId === seasonId).length;
    let duration = '—';
    if (sn.startDate && sn.endDate) {
      const days = Math.round((new Date(sn.endDate + 'T00:00:00') - new Date(sn.startDate + 'T00:00:00')) / 86400000) + 1;
      if (!isNaN(days) && days > 0) duration = days + ' day' + (days !== 1 ? 's' : '');
    }
    document.getElementById('szn-view-title').textContent = sn.name;
    document.getElementById('szn-view-body').innerHTML = `
      <div class="szn-view-row"><span>Status</span><b><span class="pgn-badge szn-badge--${status}">${SEASON_STATUS_META[status].label}</span></b></div>
      <div class="szn-view-row"><span>Start Date</span><b>${fmtSeasonDate(sn.startDate)}</b></div>
      <div class="szn-view-row"><span>End Date</span><b>${fmtSeasonDate(sn.endDate)}</b></div>
      <div class="szn-view-row"><span>Duration</span><b>${duration}</b></div>
      <div class="szn-view-row"><span>Breeding Pairs</span><b>${pairCount}</b></div>
      <div class="szn-view-row"><span>Trainings</span><b>${trainingCount}</b></div>
      <div class="szn-view-row szn-view-row--notes"><span>Notes</span><b>${sn.notes ? sn.notes : '—'}</b></div>`;
    document.getElementById('db-season-view-modal').classList.add('open');
  }
  document.getElementById('szn-view-close').onclick = () => document.getElementById('db-season-view-modal').classList.remove('open');
  document.getElementById('szn-view-edit').onclick = () => {
    const sn = seasons[viewingSeasonId];
    document.getElementById('db-season-view-modal').classList.remove('open');
    if (sn) openEditSeasonModal(viewingSeasonId, sn);
  };

  // Toolbar wiring: search, status filter, rows per page.
  (function wireSeasonsToolbar() {
    const search = document.getElementById('db-seasons-search');
    if (search) search.oninput = () => { currentSeasonPage = 1; renderSeasonsList(); };
    const statusSel = document.getElementById('db-seasons-status-filter');
    if (statusSel) statusSel.onchange = (e) => { seasonStatusFilter = e.target.value; currentSeasonPage = 1; renderSeasonsList(); };
    const perPage = document.getElementById('db-seasons-per-page');
    if (perPage) perPage.onchange = (e) => {
      seasonsPerPage = e.target.value === 'all' ? Infinity : parseInt(e.target.value, 10);
      currentSeasonPage = 1;
      renderSeasonsList();
    };
  })();

  // Only one season is ever active at a time -- batched so the old active
  // season's flag clears in the same write as the new one's, never a
  // moment with zero or two active seasons.
  async function setActiveSeason(seasonId) {
    const batch = db.batch();
    Object.keys(seasons).forEach(id => {
      if (seasons[id].isActive && id !== seasonId) batch.update(db.collection('seasons').doc(id), { isActive: false, status: 'completed' });
    });
    batch.update(db.collection('seasons').doc(seasonId), { isActive: true, status: 'active' });
    try {
      await batch.commit();
      // Update the local cache immediately instead of waiting on the
      // onSnapshot round-trip -- keeps the UI in sync with the write even
      // if the realtime listener is slow or briefly stuck.
      Object.keys(seasons).forEach(id => {
        const wasActive = !!seasons[id].isActive;
        seasons[id].isActive = (id === seasonId);
        if (id === seasonId) seasons[id].status = 'active';
        else if (wasActive) seasons[id].status = 'completed';
      });
      renderSeasonsList();
      renderPairsList();
      showToast('✅ Active season updated');
    }
    catch (e) {
      console.error('Set active season failed:', e);
      showToast('❌ Could not update: ' + e.message);
    }
  }

  function updateSeasonStatusDot() {
    const sel = document.getElementById('db-season-status-select');
    const dot = document.getElementById('db-season-status-dot');
    if (sel && dot && SEASON_STATUS_META[sel.value]) dot.style.color = SEASON_STATUS_META[sel.value].color;
  }
  function updateSeasonNotesCount() {
    document.getElementById('db-season-notes-count').textContent = document.getElementById('db-season-notes').value.length;
  }
  document.getElementById('db-season-status-select').addEventListener('change', updateSeasonStatusDot);
  document.getElementById('db-season-notes').addEventListener('input', updateSeasonNotesCount);

  function openNewSeasonModal() {
    editingSeasonId = null;
    document.getElementById('db-season-modal-title').textContent = 'Create New Season';
    document.getElementById('db-season-modal-subtitle').textContent = 'Add a new racing season to organize your pigeons.';
    document.getElementById('db-season-save-label').textContent = 'Create Season';
    document.getElementById('db-season-name').value = '';
    document.getElementById('db-season-start').value = '';
    document.getElementById('db-season-end').value = '';
    document.getElementById('db-season-notes').value = '';
    // first season defaults to Active, later ones to Upcoming
    document.getElementById('db-season-status-select').value = Object.keys(seasons).length === 0 ? 'active' : 'upcoming';
    document.getElementById('db-season-status').textContent = '';
    updateSeasonStatusDot();
    updateSeasonNotesCount();
    document.getElementById('db-season-modal').classList.add('open');
  }
  function openEditSeasonModal(id, sn) {
    editingSeasonId = id;
    document.getElementById('db-season-modal-title').textContent = 'Edit Season';
    document.getElementById('db-season-modal-subtitle').textContent = 'Update the name, dates, status or notes for this racing season.';
    document.getElementById('db-season-save-label').textContent = 'Save Changes';
    document.getElementById('db-season-name').value = sn.name || '';
    // Older seasons only have a year -- suggest that whole year so the
    // (now required) dates can just be confirmed.
    document.getElementById('db-season-start').value = sn.startDate || (sn.year ? sn.year + '-01-01' : '');
    document.getElementById('db-season-end').value = sn.endDate || (sn.year ? sn.year + '-12-31' : '');
    document.getElementById('db-season-notes').value = sn.notes || '';
    document.getElementById('db-season-status-select').value = seasonStatusOf(sn);
    document.getElementById('db-season-status').textContent = '';
    updateSeasonStatusDot();
    updateSeasonNotesCount();
    document.getElementById('db-season-modal').classList.add('open');
  }
  const seasonAddBtn = document.getElementById('db-add-season-btn');
  const seasonSaveBtn = document.getElementById('db-season-save');
  const seasonCancelBtn = document.getElementById('db-season-cancel');
  if (seasonAddBtn) seasonAddBtn.onclick = openNewSeasonModal;
  if (seasonCancelBtn) seasonCancelBtn.onclick = () => document.getElementById('db-season-modal').classList.remove('open');
  if (seasonSaveBtn) seasonSaveBtn.onclick = async () => {
    const statusEl = document.getElementById('db-season-status');
    try {
      const name = document.getElementById('db-season-name').value.trim();
      const startDate = document.getElementById('db-season-start').value;
      const endDate = document.getElementById('db-season-end').value;
      const notes = document.getElementById('db-season-notes').value.trim();
      const status = document.getElementById('db-season-status-select').value;
      if (!name || !startDate || !endDate) {
        statusEl.textContent = 'Season name, start date and end date are required.';
        statusEl.style.color = '#c0392b';
        showToast('⚠️ Season name, start date and end date are required');
        return;
      }
      if (endDate < startDate) {
        statusEl.textContent = 'The end date must be on or after the start date.';
        statusEl.style.color = '#c0392b';
        return;
      }
      statusEl.textContent = 'Saving...';
      statusEl.style.color = '#6b7a87';
      // "year" stays on the record (other pages label seasons as "Name (year)");
      // it simply follows the start date now.
      const year = parseInt(startDate.slice(0, 4), 10) || null;
      const data = { name, year, startDate, endDate, notes, status, orgId: currentOrg.slug, ownerUid: auth.currentUser.uid };
      if (status !== 'active') data.isActive = false;
      let savedId = editingSeasonId;
      if (editingSeasonId) {
        await db.collection('seasons').doc(editingSeasonId).update(data);
        seasons[editingSeasonId] = { ...seasons[editingSeasonId], ...data };
      } else {
        const ref = await db.collection('seasons').add({ ...data, isActive: false, createdAt: firebase.firestore.FieldValue.serverTimestamp() });
        savedId = ref.id;
        // Same reasoning as setActiveSeason() -- update the local cache
        // right away so the new season shows up immediately instead of
        // waiting on the onSnapshot round-trip.
        seasons[savedId] = { ...data, isActive: false, createdAt: { toMillis: () => Date.now() } };
      }
      if (status === 'active') await setActiveSeason(savedId);
      renderSeasonsList();
      renderPairsList();
      showToast(editingSeasonId ? '✅ Season updated' : '✅ Season created');
      document.getElementById('db-season-modal').classList.remove('open');
    } catch (e) {
      console.error('Season save failed:', e);
      statusEl.textContent = e.message;
      statusEl.style.color = '#c0392b';
      showToast('❌ Could not save season: ' + e.message);
    }
  };

  // ---- PAIRS: which cock is paired with which hen, for the active season.
  let breedingPairs = {}; // pairId -> data

  function listenForBreedingPairs() {
    // Filtered by ownerUid, not orgId -- same reasoning as listenForSeasons() above.
    const query = isAdmin ? db.collection('breedingPairs') : db.collection('breedingPairs').where('ownerUid', '==', auth.currentUser.uid);
    query.onSnapshot(snapshot => {
      breedingPairs = {};
      snapshot.forEach(doc => { breedingPairs[doc.id] = doc.data(); });
      document.getElementById('db-nav-pairs-count').textContent = Object.keys(breedingPairs).length || '';
      renderPairsList();
    }, error => {
      console.error('Breeding pairs listener error:', error);
      showToast('⚠️ Could not load pairs: ' + error.message);
    });
  }

  // Dismissible info banner on the Seasons panel -- same "close and
  // remember" pattern as the loft reminder banners elsewhere in the app.
  setupDismissableBanners(['seasons-info-banner'], 'seasons-banner-restore-btn', 'fpl-seasons-banners-dismissed');
  setupDismissableBanners(['pigeon-info-banner'], 'pigeon-banner-restore-btn', 'fpl-pigeon-banners-dismissed');
  // The two Pigeon List banners are mutually exclusive by tab: the general
  // roster banner only makes sense on Active, and the Inactive explainer
  // only on Inactive -- showing both stacked was redundant. Each still
  // remembers its own dismissal on top of that.
  function updatePigeonInactiveBannerVisibility() {
    const generalEl = document.getElementById('pigeon-info-banner');
    const inactiveEl = document.getElementById('pigeon-inactive-banner');
    const onInactiveTab = pigeonArchiveTab === 'archived';
    const generalDismissed = localStorage.getItem('fpl-pigeon-banners-dismissed') === '1';
    if (generalEl) generalEl.style.display = (!onInactiveTab && !generalDismissed) ? '' : 'none';
    if (inactiveEl) {
      const inactiveDismissed = localStorage.getItem('fpl-pigeon-inactive-banner-dismissed') === '1';
      inactiveEl.style.display = (onInactiveTab && !inactiveDismissed) ? 'flex' : 'none';
    }
    // The restore icon only makes sense on Active (that's the only tab the
    // general banner it restores belongs to) and only once that banner has
    // actually been dismissed.
    const restoreBtn = document.getElementById('pigeon-banner-restore-btn');
    if (restoreBtn) restoreBtn.style.display = (!onInactiveTab && generalDismissed) ? '' : 'none';
  }
  const pigeonInactiveBanner = document.getElementById('pigeon-inactive-banner');
  if (pigeonInactiveBanner) {
    const closeBtn = pigeonInactiveBanner.querySelector('.db-notice-close');
    if (closeBtn) closeBtn.onclick = () => {
      try { localStorage.setItem('fpl-pigeon-inactive-banner-dismissed', '1'); } catch (e) {}
      updatePigeonInactiveBannerVisibility();
    };
  }
  setupDismissableBanners(['pairs-info-banner'], 'pairs-banner-restore-btn', 'fpl-pairs-banners-dismissed');
  setupDismissableBanners(['trk-info-banner'], 'trk-banner-restore-btn', 'fpl-trk-banners-dismissed');

  let pairsPerPage = 20;
  let currentPairPage = 1;
  let pairSeasonFilter = 'all'; // 'all' | 'active' | a specific seasonId

  // Keeps the season filter's own <option> list in sync with real seasons
  // (name changes, new/deleted seasons) without losing whatever the user
  // currently has selected, same idea as re-filling any other dropdown
  // that's backed by live data.
  function populatePairSeasonFilter() {
    const el = document.getElementById('db-pairs-season-filter');
    if (!el) return;
    const prevValue = el.value;
    const seasonIds = Object.keys(seasons).sort((a, b) => (seasons[b].year || 0) - (seasons[a].year || 0));
    el.innerHTML = '<option value="all">All Pairs</option><option value="active">Active Season</option>' +
      seasonIds.map(id => `<option value="${id}">${seasons[id].name} (${seasons[id].year})</option>`).join('');
    if ([...el.options].some(o => o.value === prevValue)) el.value = prevValue;
  }

  // ---- Breeding Pairs page: summary cards, filterable card grid, and a
  // details view (same panel, list <-> detail sub-views, like Group Tracking).
  // Everything is derived from data that already exists: the pair record
  // (cock, hen, season, box, notes, created date), the pigeon list, and the
  // seasons list. "Offspring" = this org's pigeons whose Sire/Dam are this
  // pair's cock and hen (exactly what "Add Youngster" sets up). A pair counts
  // as Active while its Season is the currently active one.
  let openPairId = null;
  let pairStatusFilter = 'all'; // 'all' | 'active' | 'inactive'
  let pairSort = 'name'; // 'name' | 'season' | 'paired' | 'offspring'
  let pairDetailTab = 'offspring'; // 'offspring' | 'history' | 'notes'

  // DD Mon YYYY with fixed 3-letter months (toLocaleDateString gives 'Sept' in some browsers).
  function fmtDateOnly(ms) {
    const d = new Date(ms);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return String(d.getDate()).padStart(2, '0') + ' ' + months[d.getMonth()] + ' ' + d.getFullYear();
  }
  function pairParents(pair) {
    return {
      cock: findPigeonDoc(pair.cockId) || null,
      hen: findPigeonDoc(pair.henId) || null
    };
  }
  function pairNameOf(pair) {
    const { cock, hen } = pairParents(pair);
    return (cock ? cock.p.name : 'Unknown') + ' × ' + (hen ? hen.p.name : 'Unknown');
  }
  function pairIsActive(pair) {
    const season = seasons[pair.seasonId];
    return !!(season && season.isActive);
  }
  function pairOffspring(pair) {
    return allPigeonDocs
      .filter(d => d.p.sireId === pair.cockId && d.p.damId === pair.henId)
      .sort((a, b) => (new Date(b.p.dateOfBirth || 0).getTime() || 0) - (new Date(a.p.dateOfBirth || 0).getTime() || 0));
  }
  function isoLocalDate(ms) {
    const d = new Date(ms);
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }
  // The pair's Pairing Date when one was entered; pairs made before that
  // field existed fall back to the day their record was created.
  function pairPairedMs(pair) {
    if (pair.pairingDate) {
      const t = new Date(pair.pairingDate + 'T00:00:00').getTime();
      if (!isNaN(t)) return t;
    }
    return tsMs(pair.createdAt) || 0;
  }
  function pairPhotoHtml(doc, extraClass) {
    const p = doc ? doc.p : null;
    const initials = p ? ((p.name || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?') : '?';
    const color = (p && p.markerColor) || '#8a97a3';
    return '<div class="prs-photo ' + (extraClass || '') + '" style="background:' + color + ';">' +
      '<span>' + initials + '</span>' +
      (p && p.photoURL ? '<img src="' + p.photoURL + '" alt="" onerror="this.style.display=\'none\'">' : '') +
    '</div>';
  }
  function sexIconHtml(sex) {
    return sex === 'cock' ? '<i class="fa-solid fa-mars" style="color:#2f6fed;"></i>'
      : sex === 'hen' ? '<i class="fa-solid fa-venus" style="color:#d6336c;"></i>'
      : '<i class="fa-solid fa-circle-question"></i>';
  }
  function statusBadgeHtml(isActive) {
    return '<span class="pgn-badge ' + (isActive ? 'pgn-badge--active' : 'pgn-badge--inactive') + '">' + (isActive ? 'Active' : 'Inactive') + '</span>';
  }

  function renderPairsSummary() {
    const ids = Object.keys(breedingPairs);
    const activeCount = ids.filter(id => pairIsActive(breedingPairs[id])).length;
    const offspringIds = new Set();
    ids.forEach(id => pairOffspring(breedingPairs[id]).forEach(d => offspringIds.add(d.id)));
    // Next Season = the earliest season year after the active one (or after
    // last year, if none is active); shows a dash when none is set up yet.
    const activeSeason = seasons[activeSeasonId()];
    const baseYear = activeSeason ? (activeSeason.year || 0) : new Date().getFullYear() - 1;
    const laterYears = Object.keys(seasons).map(id => seasons[id]).filter(x => !x.isActive && (x.year || 0) > baseYear).map(x => x.year);
    const set = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
    set('prs-stat-pairs', ids.length);
    set('prs-stat-offspring', offspringIds.size);
    set('prs-stat-active', ids.length ? Math.round(activeCount / ids.length * 100) + '%' : '0%');
    set('prs-stat-next', laterYears.length ? Math.min.apply(null, laterYears) : '—');
  }

  function renderPairsList() {
    renderPairsSummary();
    if (openPairId) {
      if (breedingPairs[openPairId]) renderPairDetail(); else closePairDetail();
    }
    const grid = document.getElementById('db-pairs-grid');
    const empty = document.getElementById('db-pairs-empty');
    populatePairSeasonFilter();
    // Shows every pair regardless of season by default -- a hard "only the
    // active season's pairs" filter (tried first, matching the
    // competitor's own wording) meant deleting the active season made
    // every pair using it completely unreachable in the UI, with no way
    // back short of editing Firestore directly. The season dropdown above
    // is an optional filter now, not a visibility gate.
    const hasSeasons = Object.keys(seasons).length > 0;
    const term = (document.getElementById('db-pairs-search') || {}).value || '';
    const searchTerm = term.trim().toLowerCase();
    const activeId = activeSeasonId();
    let pairIds = Object.keys(breedingPairs).filter(pairId => {
      const pair = breedingPairs[pairId];
      if (pairSeasonFilter === 'active' && pair.seasonId !== activeId) return false;
      if (pairSeasonFilter !== 'all' && pairSeasonFilter !== 'active' && pair.seasonId !== pairSeasonFilter) return false;
      if (pairStatusFilter === 'active' && !pairIsActive(pair)) return false;
      if (pairStatusFilter === 'inactive' && pairIsActive(pair)) return false;
      if (!searchTerm) return true;
      const { cock, hen } = pairParents(pair);
      return pairNameOf(pair).toLowerCase().includes(searchTerm) ||
        ((cock && cock.p.ringNumber) || '').toLowerCase().includes(searchTerm) ||
        ((hen && hen.p.ringNumber) || '').toLowerCase().includes(searchTerm);
    });
    pairIds.sort((a, b) => {
      const pa = breedingPairs[a], pb = breedingPairs[b];
      if (pairSort === 'season') return ((seasons[pb.seasonId] || {}).year || 0) - ((seasons[pa.seasonId] || {}).year || 0);
      if (pairSort === 'paired') return pairPairedMs(pb) - pairPairedMs(pa);
      if (pairSort === 'offspring') return pairOffspring(pb).length - pairOffspring(pa).length;
      return pairNameOf(pa).toLowerCase().localeCompare(pairNameOf(pb).toLowerCase());
    });
    const totalPairCount = Object.keys(breedingPairs).length;
    const ownCocks = allPigeonDocs.filter(d => d.p.orgId === currentOrg.slug && d.p.sex === 'cock');
    const ownHens = allPigeonDocs.filter(d => d.p.orgId === currentOrg.slug && d.p.sex === 'hen');
    // cocks/hens = pigeons in this loft AND ancestors saved from imported pedigrees (same lists the pair form offers)
    const canPair = (ownCocks.length > 0 || parentChoices('cock').length > 0) && (ownHens.length > 0 || parentChoices('hen').length > 0) && hasSeasons;
    document.getElementById('db-pairs-count').textContent = totalPairCount ? `(${totalPairCount})` : '';
    document.getElementById('db-add-pair-btn').style.display = ''; // always shown -- clicking it explains what is missing (see openNewPairModal)
    // Same reasoning as Pigeon List/Trackers: the per-page selector is just
    // clutter for a handful of pairs -- only show it once there's actually
    // enough to paginate meaningfully.
    const rowsWrap = document.getElementById('pairs-rows-wrap');
    if (rowsWrap) rowsWrap.style.display = totalPairCount > 20 ? '' : 'none';
    const showingEl = document.getElementById('db-pairs-showing');
    if (showingEl) showingEl.textContent = '';

    if (pairIds.length === 0) {
      empty.style.display = 'block';
      grid.innerHTML = '';
      document.getElementById('db-pairs-pagination').innerHTML = '';
      document.getElementById('db-pairs-empty-title').textContent = totalPairCount === 0
        ? 'No breeding pairs found.'
        : 'No pairs match your search/filter.';
      document.getElementById('db-pairs-empty-text').textContent = !hasSeasons
        ? 'Create a season under Manage Seasons first.'
        : !canPair
          ? 'You need both cocks and hens to create breeding pairs.'
          : totalPairCount === 0
            ? 'Click "+ Add Breeding Pair" above to create your first one.'
            : 'Try a different search term or filter.';
      document.getElementById('db-pairs-empty-btn').style.display = (!hasSeasons || !canPair) ? '' : 'none';
      document.getElementById('db-pairs-empty-btn').textContent = !hasSeasons ? '📅 Manage Seasons' : '+ Add Pigeons';
      return;
    }
    empty.style.display = 'none';

    const totalPages = pairsPerPage === Infinity ? 1 : Math.max(1, Math.ceil(pairIds.length / pairsPerPage));
    if (currentPairPage > totalPages) currentPairPage = totalPages;
    const startIdx = pairsPerPage === Infinity ? 0 : (currentPairPage - 1) * pairsPerPage;
    const pageIds = pairsPerPage === Infinity ? pairIds : pairIds.slice(startIdx, startIdx + pairsPerPage);

    renderPairsPagination(totalPages);
    if (showingEl) {
      showingEl.textContent = 'Showing ' + (startIdx + 1) + ' to ' + (startIdx + pageIds.length) + ' of ' + pairIds.length +
        ' pair' + (pairIds.length !== 1 ? 's' : '');
    }
    grid.innerHTML = '';
    pageIds.forEach(pairId => {
      const pair = breedingPairs[pairId];
      const { cock, hen } = pairParents(pair);
      const offspringCount = pairOffspring(pair).length;
      const pairedMs = pairPairedMs(pair);
      const parentBlock = (doc, sex) => {
        const p = doc ? doc.p : null;
        return `
          <div class="prs-parent">
            ${pairPhotoHtml(doc)}
            <div class="prs-parent-name">${sexIconHtml(sex)} <span>${p ? p.name : 'Unknown'}</span></div>
            <div class="prs-parent-meta">${p && p.ringNumber ? p.ringNumber : 'No ring number'}</div>
            <div class="prs-parent-meta">${p && p.color ? p.color : '—'}</div>
          </div>`;
      };
      const card = document.createElement('div');
      card.className = 'prs-card';
      card.innerHTML = `
        <div class="prs-card-head">
          <div class="prs-card-title">${pairNameOf(pair)}</div>
          ${statusBadgeHtml(pairIsActive(pair))}
          <button type="button" class="ltc-kebab-btn pgn-kebab" aria-label="More actions"><i class="fa-solid fa-ellipsis-vertical"></i></button>
        </div>
        <div class="ltc-kebab-menu">
          <div class="ltc-kebab-menu-item pair-view-item"><i class="fa-solid fa-eye"></i> View Pair</div>
          <div class="ltc-kebab-menu-item pair-edit-item"><i class="fa-solid fa-pen"></i> Edit Pair</div>
          <div class="ltc-kebab-menu-item pair-youngster-item"><i class="fa-solid fa-dove"></i> Add Youngster</div>
          <div class="ltc-kebab-menu-divider"></div>
          <div class="ltc-kebab-menu-item danger pair-delete-item"><i class="fa-solid fa-trash"></i> Delete Pair</div>
        </div>
        <div class="prs-card-body">
          ${parentBlock(cock, 'cock')}
          <div class="prs-x">×</div>
          ${parentBlock(hen, 'hen')}
        </div>
        <div class="prs-card-foot">
          <span class="prs-foot-item"><i class="fa-solid fa-egg"></i> ${offspringCount} Offspring</span>
          <span class="prs-foot-item"><i class="fa-regular fa-calendar"></i> Paired: ${pairedMs ? fmtDateOnly(pairedMs) : '—'}</span>
          <button type="button" class="prs-go pair-view-btn" aria-label="View pair details"><i class="fa-solid fa-chevron-right"></i></button>
        </div>
      `;
      const kebabBtn = card.querySelector('.ltc-kebab-btn');
      const kebabMenu = card.querySelector('.ltc-kebab-menu');
      kebabBtn.onclick = (e) => {
        e.stopPropagation();
        const wasOpen = kebabMenu.classList.contains('open');
        document.querySelectorAll('.ltc-kebab-menu.open').forEach(m => m.classList.remove('open'));
        if (!wasOpen) { kebabMenu.classList.add('open'); keepKebabMenuInView(kebabMenu); }
      };
      // Whole card opens the details, except the kebab button/menu.
      card.onclick = (e) => {
        if (e.target.closest('.ltc-kebab-btn, .ltc-kebab-menu')) return;
        showPairDetail(pairId);
      };
      card.querySelector('.pair-view-item').onclick = () => { kebabMenu.classList.remove('open'); showPairDetail(pairId); };
      card.querySelector('.pair-edit-item').onclick = () => { kebabMenu.classList.remove('open'); openEditPairModal(pairId, pair); };
      card.querySelector('.pair-youngster-item').onclick = () => { kebabMenu.classList.remove('open'); addYoungsterForPair(pair); };
      card.querySelector('.pair-delete-item').onclick = () => { kebabMenu.classList.remove('open'); deletePair(pairId); };
      grid.appendChild(card);
    });
  }

  async function deletePair(pairId) {
    if (!(await showConfirmModal('Delete this breeding pair? This only removes the pair record -- both pigeons stay as-is.', 'Delete pair?'))) return;
    try {
      await db.collection('breedingPairs').doc(pairId).delete();
      delete breedingPairs[pairId];
      if (openPairId === pairId) closePairDetail();
      renderPairsList();
    }
    catch (e) { showToast('Could not delete: ' + e.message); }
  }

  // Opens the normal Add Pigeon modal with Sire/Dam already set from
  // this pair -- reuses the exact same Parents card instead of a
  // separate "add youngster" flow.
  function addYoungsterForPair(pair) {
    openAddModal();
    document.getElementById('db-pigeon-sire').value = pair.cockId;
    document.getElementById('db-pigeon-dam').value = pair.henId;
    setParentsCardOpen(true);
  }

  function showPairDetail(pairId) {
    openPairId = pairId;
    pairDetailTab = 'offspring';
    document.getElementById('pairs-list-view').style.display = 'none';
    document.getElementById('pairs-detail-view').style.display = 'block';
    const contentEl = document.querySelector('.db-content');
    if (contentEl) contentEl.scrollTop = 0;
    renderPairDetail();
  }
  function closePairDetail() {
    openPairId = null;
    const list = document.getElementById('pairs-list-view');
    const detail = document.getElementById('pairs-detail-view');
    if (detail) detail.style.display = 'none';
    if (list) list.style.display = 'block';
  }

  function renderPairDetail() {
    const pair = breedingPairs[openPairId];
    if (!pair) return;
    const { cock, hen } = pairParents(pair);
    const offspring = pairOffspring(pair);
    const activeOffspring = offspring.filter(d => !d.p.isArchived);
    const pairedMs = pairPairedMs(pair);
    const season = seasons[pair.seasonId];

    document.getElementById('pair-detail-title').textContent = pairNameOf(pair);
    const detailBadge = document.getElementById('pair-detail-status');
    detailBadge.className = 'pgn-badge ' + (pairIsActive(pair) ? 'pgn-badge--active' : 'pgn-badge--inactive');
    detailBadge.textContent = pairIsActive(pair) ? 'Active' : 'Inactive';

    const parentCard = (doc, sex) => {
      const p = doc ? doc.p : null;
      const age = p ? pigeonAgeLabel(p.dateOfBirth) : null;
      return `
        <div class="prs-parent-card">
          ${pairPhotoHtml(doc)}
          <div class="prs-parent-info">
            <div class="prs-parent-title">${sexIconHtml(sex)} ${p ? p.name : 'Unknown'}</div>
            <div class="prs-parent-line">Ring: ${p && p.ringNumber ? p.ringNumber : '—'}</div>
            <div class="prs-parent-line">Color: ${p && p.color ? p.color : '—'}</div>
            <div class="prs-parent-line">Age: ${age || '—'}</div>
            <div class="prs-parent-line">Status: ${p ? statusBadgeHtml(!p.isArchived) : '—'}</div>
          </div>
        </div>`;
    };
    document.getElementById('pair-detail-cock').innerHTML = parentCard(cock, 'cock');
    document.getElementById('pair-detail-hen').innerHTML = parentCard(hen, 'hen');
    document.getElementById('pair-detail-facts').innerHTML = `
      <div class="prs-fact"><i class="fa-regular fa-calendar"></i><div><span>Paired On</span><b>${pairedMs ? fmtDateOnly(pairedMs) : '—'}</b></div></div>
      <div class="prs-fact"><i class="fa-solid fa-house"></i><div><span>Nest / Box</span><b>${pair.box ? pair.box : '—'}</b></div></div>
      <div class="prs-fact"><i class="fa-solid fa-egg"></i><div><span>Total Offspring</span><b>${offspring.length}</b></div></div>
      <div class="prs-fact"><i class="fa-solid fa-chart-simple"></i><div><span>Active Offspring</span><b>${activeOffspring.length}</b></div></div>
    `;

    document.querySelectorAll('#pairs-detail-view .prs-tab').forEach(t => t.classList.toggle('active', t.dataset.pairTab === pairDetailTab));
    ['offspring', 'history', 'notes'].forEach(k => {
      document.getElementById('pair-tab-' + k).style.display = k === pairDetailTab ? 'block' : 'none';
    });

    // ---- Offspring
    const kidsEl = document.getElementById('pair-offspring-list');
    document.getElementById('pair-offspring-count').textContent = '(' + offspring.length + ')';
    if (offspring.length === 0) {
      kidsEl.innerHTML = `
        <div class="prs-empty-box">
          <div class="prs-empty-icon"><i class="fa-solid fa-egg"></i></div>
          <b>Add a youngster to this pair</b>
          <p>Record a new pigeon produced from ${pairNameOf(pair)}.</p>
          <button type="button" class="db-btn-primary pgn-add-btn pair-add-youngster-btn"><i class="fa-solid fa-plus"></i> Add Youngster</button>
        </div>`;
    } else {
      kidsEl.innerHTML = '<div class="prs-kids">' + offspring.map(d => {
        const p = d.p;
        const hatched = p.dateOfBirth ? fmtDateOnly(new Date(p.dateOfBirth).getTime()) : '—';
        const sexLabel = p.sex === 'cock' ? 'Cock' : p.sex === 'hen' ? 'Hen' : 'Sex unknown';
        return `
          <div class="prs-kid" data-id="${d.id}">
            ${pairPhotoHtml(d)}
            <div class="prs-kid-info">
              <div class="prs-kid-top">
                <div class="prs-kid-name">${p.name}</div>
                <button type="button" class="ltc-kebab-btn pgn-kebab prs-kid-kebab" aria-label="More actions"><i class="fa-solid fa-ellipsis-vertical"></i></button>
              </div>
              <div class="prs-kid-line">Ring: ${p.ringNumber || '—'}</div>
              <div class="prs-kid-line">${sexIconHtml(p.sex)} ${sexLabel}</div>
              <div class="prs-kid-line"><i class="fa-solid fa-palette"></i> Color: ${p.color || '—'}</div>
              <div class="prs-kid-line"><i class="fa-regular fa-calendar"></i> Hatched: ${hatched}</div>
              <div class="prs-kid-status">${statusBadgeHtml(!p.isArchived)}</div>
            </div>
            <div class="ltc-kebab-menu">
              <div class="ltc-kebab-menu-item prs-kid-view"><i class="fa-solid fa-sitemap"></i> View Pedigree</div>
              <div class="ltc-kebab-menu-item prs-kid-edit"><i class="fa-solid fa-pen"></i> Edit Pigeon</div>
            </div>
          </div>`;
      }).join('') + '</div>';
      kidsEl.querySelectorAll('.prs-kid').forEach(el => {
        const doc = allPigeonDocs.find(x => x.id === el.dataset.id);
        const menu = el.querySelector('.ltc-kebab-menu');
        el.querySelector('.prs-kid-kebab').onclick = (e) => {
          e.stopPropagation();
          const wasOpen = menu.classList.contains('open');
          document.querySelectorAll('.ltc-kebab-menu.open').forEach(m => m.classList.remove('open'));
          if (!wasOpen) { menu.classList.add('open'); keepKebabMenuInView(menu); }
        };
        el.querySelector('.prs-kid-view').onclick = () => { menu.classList.remove('open'); if (doc) openPigeonPedigreePage(doc.id, doc.p, 'pairs'); };
        el.querySelector('.prs-kid-edit').onclick = () => { menu.classList.remove('open'); if (doc) openEditModal(doc.id, doc.p); };
      });
    }

    // ---- Breeding History: a timeline built from what's on record -- the
    // pairing itself, then each youngster's hatch date.
    const events = [];
    events.push({
      ms: pairedMs || 0, icon: 'fa-heart', color: '#e5537e',
      title: 'Paired', text: pairNameOf(pair) + (season ? ' — ' + season.name + ' (' + season.year + ')' : '') + (pair.box ? ' · Box ' + pair.box : '')
    });
    offspring.forEach(d => {
      const t = d.p.dateOfBirth ? new Date(d.p.dateOfBirth).getTime() : 0;
      events.push({ ms: isNaN(t) ? 0 : t, icon: 'fa-egg', color: '#19a974', title: 'Youngster hatched', text: d.p.name + (d.p.ringNumber ? ' (' + d.p.ringNumber + ')' : '') });
    });
    events.sort((a, b) => (a.ms || Infinity) - (b.ms || Infinity));
    document.getElementById('pair-history-list').innerHTML = events.map(ev => `
      <div class="prs-tl-item">
        <span class="prs-tl-dot" style="background:${ev.color};"><i class="fa-solid ${ev.icon}"></i></span>
        <div class="prs-tl-body"><b>${ev.title}</b><span>${ev.text}</span></div>
        <div class="prs-tl-date">${ev.ms ? fmtDateOnly(ev.ms) : 'Date not recorded'}</div>
      </div>`).join('');

    // ---- Notes
    const notesEl = document.getElementById('pair-notes-body');
    notesEl.innerHTML = (pair.box ? '<p class="prs-note-meta"><i class="fa-solid fa-box"></i> Box ' + pair.box + '</p>' : '') +
      (pair.notes ? '<p class="prs-note-text">' + pair.notes + '</p>' : '<p class="prs-note-empty">No notes for this pair yet.</p>');
  }

  // Detail-view wiring -- static buttons once, dynamic ones by delegation.
  (function wirePairDetail() {
    const view = document.getElementById('pairs-detail-view');
    if (!view) return;
    document.getElementById('pair-detail-back').onclick = () => closePairDetail();
    document.getElementById('pair-detail-edit').onclick = () => { const pr = breedingPairs[openPairId]; if (pr) openEditPairModal(openPairId, pr); };
    document.getElementById('pair-detail-delete').onclick = () => { if (openPairId) deletePair(openPairId); };
    view.addEventListener('click', (e) => {
      const tab = e.target.closest('.prs-tab');
      if (tab) { pairDetailTab = tab.dataset.pairTab; renderPairDetail(); return; }
      if (e.target.closest('.pair-add-youngster-btn')) {
        const pr = breedingPairs[openPairId];
        if (pr) addYoungsterForPair(pr);
        return;
      }
      if (e.target.closest('.pair-edit-notes-btn')) {
        const pr = breedingPairs[openPairId];
        if (pr) openEditPairModal(openPairId, pr);
      }
    });
  })();

  function renderPairsPagination(totalPages) {
    const el = document.getElementById('db-pairs-pagination');
    if (!el) return;
    if (totalPages <= 1) {
      el.innerHTML = '';
      return;
    }
    el.innerHTML = '';

    const prevBtn = document.createElement('button');
    prevBtn.textContent = '← Prev';
    prevBtn.disabled = currentPairPage === 1;
    prevBtn.onclick = () => { currentPairPage--; renderPairsList(); };
    el.appendChild(prevBtn);

    for (let i = 1; i <= totalPages; i++) {
      const pageBtn = document.createElement('button');
      pageBtn.textContent = i;
      if (i === currentPairPage) pageBtn.className = 'active';
      pageBtn.onclick = () => { currentPairPage = i; renderPairsList(); };
      el.appendChild(pageBtn);
    }

    const nextBtn = document.createElement('button');
    nextBtn.textContent = 'Next →';
    nextBtn.disabled = currentPairPage === totalPages;
    nextBtn.onclick = () => { currentPairPage++; renderPairsList(); };
    el.appendChild(nextBtn);
  }

  const pairsSearchEl = document.getElementById('db-pairs-search');
  if (pairsSearchEl) pairsSearchEl.oninput = () => { currentPairPage = 1; renderPairsList(); };
  const pairsPerPageEl = document.getElementById('db-pairs-per-page');
  if (pairsPerPageEl) pairsPerPageEl.onchange = (e) => {
    pairsPerPage = e.target.value === 'all' ? Infinity : parseInt(e.target.value, 10);
    currentPairPage = 1;
    renderPairsList();
  };
  const pairsStatusFilterEl = document.getElementById('pairs-status-filter');
  if (pairsStatusFilterEl) pairsStatusFilterEl.onchange = (e) => {
    pairStatusFilter = e.target.value;
    currentPairPage = 1;
    renderPairsList();
  };
  const pairsSortEl = document.getElementById('pairs-sort');
  if (pairsSortEl) pairsSortEl.onchange = (e) => {
    pairSort = e.target.value;
    currentPairPage = 1;
    renderPairsList();
  };
  const pairsSeasonFilterEl = document.getElementById('db-pairs-season-filter');
  if (pairsSeasonFilterEl) pairsSeasonFilterEl.onchange = (e) => {
    pairSeasonFilter = e.target.value;
    currentPairPage = 1;
    renderPairsList();
  };

  function populatePairSelects() {
    document.getElementById('db-pair-cock').innerHTML = '<option value="">Select a cock...</option>' + parentOptionsHtml(parentChoices('cock'));
    document.getElementById('db-pair-hen').innerHTML = '<option value="">Select a hen...</option>' + parentOptionsHtml(parentChoices('hen'));
    const seasonIds = Object.keys(seasons).sort((a, b) => (seasons[b].year || 0) - (seasons[a].year || 0));
    document.getElementById('db-pair-season').innerHTML = '<option value="">Select a season...</option>' +
      seasonIds.map(id => `<option value="${id}">${seasons[id].name} (${seasons[id].year})</option>`).join('');
  }

  let editingPairId = null;

  async function openNewPairModal() {
    // Not enough to pair yet? Say exactly what is missing instead of hiding the button.
    const hasSeasonsNow = Object.keys(seasons).length > 0;
    const cocksNow = parentChoices('cock').length, hensNow = parentChoices('hen').length;
    if (!hasSeasonsNow) {
      if (await showConfirmModal('A breeding pair belongs to a season, and you have not created one yet. Create a season first, then come back to add your pair.', 'Create a season first', 'Go to Seasons')) activateTab('seasons');
      return;
    }
    if (!cocksNow || !hensNow) {
      const missing = !cocksNow && !hensNow ? 'a cock (male) and a hen (female)' : !cocksNow ? 'a cock (male)' : 'a hen (female)';
      if (await showConfirmModal('To create a breeding pair you need ' + missing + ' in your loft. Add the pigeon and set its sex to ' + (!cocksNow && !hensNow ? 'Cock or Hen' : !cocksNow ? 'Cock' : 'Hen') + ' (or import a pedigree that has one), then try again.', 'No ' + (!cocksNow && !hensNow ? 'cock or hen' : !cocksNow ? 'cock' : 'hen') + ' available', 'Go to Pigeon List')) activateTab('pigeon-list');
      return;
    }
    editingPairId = null;
    populatePairSelects();
    document.getElementById('db-pair-modal-title').textContent = 'Create Breeding Pair';
    document.getElementById('db-pair-modal-subtitle').textContent = 'Select a season and choose the sire and dam to create a new breeding pair.';
    document.getElementById('db-pair-save-label').textContent = 'Create Breeding Pair';
    const seasonId = activeSeasonId();
    if (seasonId) document.getElementById('db-pair-season').value = seasonId;
    document.getElementById('db-pair-date').value = isoLocalDate(Date.now());
    document.getElementById('db-pair-box').value = '';
    document.getElementById('db-pair-notes').value = '';
    document.getElementById('db-pair-status').textContent = '';
    document.getElementById('db-pair-modal').classList.add('open');
  }
  function openEditPairModal(pairId, pair) {
    editingPairId = pairId;
    populatePairSelects();
    document.getElementById('db-pair-modal-title').textContent = 'Edit Breeding Pair';
    document.getElementById('db-pair-modal-subtitle').textContent = 'Update the season, sire, dam, box or notes for this breeding pair.';
    document.getElementById('db-pair-save-label').textContent = 'Save Changes';
    pdiEnsureVirtualParentOption('db-pair-cock', pair.cockId);
    pdiEnsureVirtualParentOption('db-pair-hen', pair.henId);
    document.getElementById('db-pair-cock').value = pair.cockId || '';
    document.getElementById('db-pair-hen').value = pair.henId || '';
    document.getElementById('db-pair-season').value = pair.seasonId || '';
    const existingPairedMs = pairPairedMs(pair);
    document.getElementById('db-pair-date').value = existingPairedMs ? isoLocalDate(existingPairedMs) : '';
    document.getElementById('db-pair-box').value = pair.box || '';
    document.getElementById('db-pair-notes').value = pair.notes || '';
    document.getElementById('db-pair-status').textContent = '';
    document.getElementById('db-pair-modal').classList.add('open');
  }
  document.getElementById('db-add-pair-btn').onclick = openNewPairModal;
  // The empty-state CTA does double duty: "+ Add Pigeons" when missing a
  // cock/hen, or "Manage Seasons" when there's no active season yet --
  // whichever renderPairsList last set its label/handler-need to.
  document.getElementById('db-pairs-empty-btn').onclick = () => {
    if (!activeSeasonId()) { activateTab('seasons'); return; }
    activateTab('pigeon-list');
    openAddModal();
  };
  document.getElementById('db-pair-cancel').onclick = () => document.getElementById('db-pair-modal').classList.remove('open');
  document.getElementById('db-pair-save').onclick = async () => {
    const cockId = document.getElementById('db-pair-cock').value;
    const henId = document.getElementById('db-pair-hen').value;
    const seasonId = document.getElementById('db-pair-season').value;
    const pairingDate = document.getElementById('db-pair-date').value;
    const box = document.getElementById('db-pair-box').value.trim();
    const notes = document.getElementById('db-pair-notes').value.trim();
    const statusEl = document.getElementById('db-pair-status');
    if (!cockId || !henId || !seasonId || !pairingDate) {
      statusEl.textContent = 'Season, sire, dam and pairing date are all required.';
      statusEl.style.color = '#c0392b';
      return;
    }
    statusEl.textContent = 'Saving...';
    statusEl.style.color = '#6b7a87';
    try {
      const data = {
        cockId, henId, seasonId, pairingDate, box, notes,
        orgId: currentOrg.slug, ownerUid: auth.currentUser.uid
      };
      if (editingPairId) {
        await db.collection('breedingPairs').doc(editingPairId).update(data);
        breedingPairs[editingPairId] = { ...breedingPairs[editingPairId], ...data };
      } else {
        const ref = await db.collection('breedingPairs').add({ ...data, createdAt: firebase.firestore.FieldValue.serverTimestamp() });
        // Update the local cache immediately instead of waiting on the
        // onSnapshot round-trip -- keeps the UI in sync with the write even
        // if the realtime listener is slow or briefly stuck.
        breedingPairs[ref.id] = data;
      }
      renderPairsList();
      showToast(editingPairId ? '✅ Pair updated' : '✅ Pair saved');
      document.getElementById('db-pair-modal').classList.remove('open');
    } catch (e) {
      console.error('Pair save failed:', e);
      statusEl.textContent = e.message;
      statusEl.style.color = '#c0392b';
      showToast('❌ Could not save pair: ' + e.message);
    }
  };

